import { AppShell, Portal, ScrollArea } from '@mantine/core';
import type { PropsWithChildren } from 'react';

import { FilterOutlineIcon, FilterSolidIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { Toolbar } from '@apple/ui/data-table/controls/Toolbar/toolbar';
import { TOOLBAR_HEIGHT_FIXED_HEADER } from '@apple/ui/data-table/controls/Toolbar/toolbar-button-props';
import { useHeaderIsFixed } from '@apple/ui/shell/contexts/header';
import {
	useRightSidebarCollapsed,
	useRightSidebarSetComponent,
} from '@apple/ui/shell/contexts/right-sidebar';

import { ToggleRightSidebarButton } from '../components/ToggleRightSidebarButton';
import { RIGHT_SIDEBAR_PORTAL_TARGET_ID } from '../constants';

type Props = PropsWithChildren;

export function RightSidebarPortal({ children }: Props) {
	const { t } = useTranslation();
	const rightSidebarCollapsed = useRightSidebarCollapsed();
	const { isFixed } = useHeaderIsFixed();
	useRightSidebarSetComponent({
		toggleButtonProps: {
			icons: {
				expand: FilterOutlineIcon,
				collapse: FilterSolidIcon,
			},
			labels: {
				expand: t('controls:dataTable.showFilters'),
				collapse: t('controls:dataTable.hideFilters'),
			},
		},
	});

	return (
		<Portal target={`#${RIGHT_SIDEBAR_PORTAL_TARGET_ID}`}>
			<AppShell.Aside data-fixed-header={isFixed}>
				<AppShell.Section>
					<Toolbar
						height={isFixed ? TOOLBAR_HEIGHT_FIXED_HEADER : undefined}
						rightSection={() => [
							rightSidebarCollapsed.isSmallerThanBreakpoint ? undefined : (
								<ToggleRightSidebarButton key='right-sidebar-toggle' />
							),
						]}
					/>
				</AppShell.Section>
				<AppShell.Section grow component={ScrollArea}>
					{children}
				</AppShell.Section>
			</AppShell.Aside>
		</Portal>
	);
}
